import { API_URL } from "../../config";
import { trackedFetch } from "../../Components/Loader/GlobalLoader";

/* ──────────────────────────────────────────────────────────────────────
   Shared API surface for the Special Events module.

   Two kinds of callers live here:
     • Admin (CRUD + status updates + export) — sends the bearer token.
     • Public guest registration page — sends NO token. The backend
       routes for /public/event/* are explicitly unauthenticated.

   Keeping both in one file means the type definitions (SpecialEvent /
   EventRegistration) are shared, so the public form and the admin
   table can never drift on field names.
   ──────────────────────────────────────────────────────────────────── */

export type EventStatus = "draft" | "active" | "closed";
export type RegistrationStatus = "registered" | "attended" | "cancelled";

export interface SpecialEvent {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  event_date: string | null;
  event_time: string | null;
  location: string | null;
  admission_text: string | null;
  banner_image: string | null;
  status: EventStatus;
  max_capacity: number | null;
  registration_enabled: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // Server-appended accessors:
  public_url: string;
  registration_count: number;
  is_full: boolean;
  active_registrations_count?: number;
}

export interface EventRegistration {
  id: number;
  event_id: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  organization: string | null;
  business_type: string | null;
  guest_count: number;
  address: string | null;
  postal_code: string | null;
  notes: string | null;
  status: RegistrationStatus;
  registered_at: string;
  created_at: string;
  event?: { id: number; title: string; slug: string; event_date?: string };
}

/** Public-facing event payload (subset of SpecialEvent). */
export interface PublicEventPayload {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  event_date: string | null;
  event_time: string | null;
  location: string | null;
  admission_text: string | null;
  banner_image: string | null;  // already resolved to full URL by the server
  is_open: boolean;
  is_full: boolean;
  closed_reason: string | null;
}

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

/* ── Admin: events CRUD ─────────────────────────────────────────────── */

export const fetchEvents = async (params?: Record<string, string>): Promise<SpecialEvent[]> => {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await trackedFetch(`${API_URL}/special-events${qs}`, { headers: authHeaders() });
  const json = await res.json();
  return json.data ?? [];
};

export const fetchEvent = async (id: number): Promise<SpecialEvent> => {
  const res = await trackedFetch(`${API_URL}/special-events/${id}`, { headers: authHeaders() });
  const json = await res.json();
  return json.data;
};

export const createEvent = async (payload: Partial<SpecialEvent>): Promise<SpecialEvent> => {
  const res = await trackedFetch(`${API_URL}/special-events`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const updateEvent = async (id: number, payload: Partial<SpecialEvent>): Promise<SpecialEvent> => {
  const res = await trackedFetch(`${API_URL}/special-events/${id}`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const closeEvent = async (id: number): Promise<SpecialEvent> => {
  const res = await trackedFetch(`${API_URL}/special-events/${id}/close`, {
    method: "POST", headers: authHeaders(),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const deleteEvent = async (id: number): Promise<void> => {
  const res = await trackedFetch(`${API_URL}/special-events/${id}`, {
    method: "DELETE", headers: authHeaders(),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
};

/** Banner upload — returns the stored path (caller passes it as banner_image). */
export const uploadBanner = async (file: File): Promise<string> => {
  const form = new FormData();
  form.append("file", file);
  const res = await trackedFetch(`${API_URL}/special-events/upload-banner`, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    body: form,
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data; // storage path
};

/**
 * Public URL the QR encodes and the share dialog displays.
 *
 * Reads `window.location.origin` so it's always correct for the
 * environment the admin is currently looking at — no APP_FRONTEND_URL
 * env var or config cache to keep in sync. The server's computed
 * `public_url` accessor is kept as a fallback for non-browser contexts
 * (e.g. confirmation emails), but the admin UI never trusts it.
 */
export const publicEventUrl = (slug: string): string =>
  `${window.location.origin}/reg/${slug}`;

/** Raw URL for the QR endpoint. Pass the desired QR-payload URL so the
 *  server encodes the same string the admin sees in the share dialog —
 *  otherwise the QR would carry the server's APP_URL-derived guess. */
const qrEndpointUrl = (id: number, size: number, encodeUrl: string): string =>
  `${API_URL}/special-events/${id}/qr-code?size=${size}&url=${encodeURIComponent(encodeUrl)}`;

/**
 * Fetch the QR code with the bearer token attached, then return an
 * object-URL the caller can drop into <img src>. The blob URL is owned
 * by the document — call URL.revokeObjectURL when done (the caller
 * does this in an effect cleanup).
 */
export const fetchQrBlobUrl = async (id: number, slug: string, size = 500): Promise<string> => {
  const res = await fetch(qrEndpointUrl(id, size, publicEventUrl(slug)), {
    headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
  });
  if (!res.ok) throw new Error(`QR fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

/**
 * Trigger a browser download of the QR SVG. Authenticated fetch into a
 * blob, then synthesize an <a download> click so the file lands on disk
 * with our chosen name. SVG (not PNG) because the backend avoids the
 * imagick PHP extension — see SpecialEventController::qrCode().
 */
export const downloadQr = async (id: number, slug: string, size = 800): Promise<void> => {
  const url = await fetchQrBlobUrl(id, slug, size);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slug}-qr.svg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Slight delay before revoke to ensure the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/* ── Admin: registrations ───────────────────────────────────────────── */

export const fetchRegistrations = async (params?: Record<string, string>): Promise<EventRegistration[]> => {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await trackedFetch(`${API_URL}/event-registrations${qs}`, { headers: authHeaders() });
  const json = await res.json();
  return json.data ?? [];
};

export const updateRegistrationStatus = async (
  id: number,
  status: RegistrationStatus
): Promise<EventRegistration> => {
  const res = await trackedFetch(`${API_URL}/event-registrations/${id}/status`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify({ status }),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const deleteRegistration = async (id: number): Promise<void> => {
  const res = await trackedFetch(`${API_URL}/event-registrations/${id}`, {
    method: "DELETE", headers: authHeaders(),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
};

/** Triggers a download in the browser — caller does `window.location = url`. */
export const exportRegistrationsUrl = (params: Record<string, string>): string => {
  const qs = new URLSearchParams(params).toString();
  return `${API_URL}/event-registrations/export?${qs}`;
};

/* ── Public: guest endpoints (NO auth) ──────────────────────────────── */

export const fetchPublicEvent = async (slug: string): Promise<PublicEventPayload> => {
  const res = await fetch(`${API_URL}/public/event/${encodeURIComponent(slug)}`);
  if (!res.ok) {
    if (res.status === 404) throw new Error("Event not found");
    throw new Error("Could not load event details.");
  }
  const json = await res.json();
  return json.data;
};

export interface PublicRegisterPayload {
  full_name: string;
  email: string;
  phone: string;
  organization?: string;
  business_type?: string;
  guest_count: number;
  postal_code: string;
  address?: string;
  notes?: string;
  /** Honeypot — leave empty. Bots will fill it; the server then silent-rejects. */
  website?: string;
}

export const submitPublicRegistration = async (
  slug: string,
  payload: PublicRegisterPayload
): Promise<{ id?: number; full_name?: string; event_name?: string }> => {
  const res = await fetch(`${API_URL}/public/event-register/${encodeURIComponent(slug)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok || !json.status) {
    // Server messages here are user-facing (capacity / duplicate / closed).
    throw new Error(json.message || "Registration failed. Please try again.");
  }
  return json.data ?? {};
};

/* ── Helpers ────────────────────────────────────────────────────────── */

/**
 * Build an absolute URL for a stored banner image path.
 *
 * NOTE on path layout: this project rewires Laravel's `public` disk to
 * `public_path()` (see backend/config/filesystems.php), so files saved
 * via `->storeAs(..., 'public')` land directly in `backend/public/...`,
 * NOT in `storage/app/public/...`. There is no `storage:link` and no
 * `/storage/` URL prefix — files are served at `{origin}/{path}`.
 *
 * Origin is derived by stripping the `/api` suffix off API_URL so the
 * same code works in dev (Laravel on :8000) and prod (go2dcc.com).
 */
export const bannerUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  // API_URL is typed string|undefined because it reads off
  // window.REACT_APP_API_URL (injected at runtime). Fall back to ""
  // so a missing config doesn't crash render — image just won't load.
  const origin = (API_URL ?? "").replace(/\/api\/?$/, "");
  return `${origin}/${path.replace(/^\//, "")}`;
};

export const formatEventDate = (iso?: string | null): string => {
  if (!iso) return "";
  // Server may return either a plain date ("2026-06-15") OR a full ISO
  // datetime ("2026-06-15T00:00:00.000000Z") depending on the casts on
  // the endpoint. Strip any time portion and parse as date-only — this
  // also dodges the timezone-shift trap where midnight UTC parsed in
  // EDT renders as the previous calendar day.
  const dateOnly = iso.split("T")[0];
  const d = new Date(dateOnly + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
};

export const STATUS_LABEL: Record<EventStatus, string> = {
  draft: "Draft",
  active: "Active",
  closed: "Closed",
};

export const STATUS_COLOR: Record<EventStatus, string> = {
  draft: "#94a3b8",
  active: "#16a34a",
  closed: "#ef4444",
};

export const REG_STATUS_COLOR: Record<RegistrationStatus, string> = {
  registered: "#3b82f6",
  attended: "#16a34a",
  cancelled: "#94a3b8",
};
